<template>
  <div class="upload-studio-page">
    <MenuBar />

    <div class="content">
      <div class="page-header">
        <div>
          <h1>Campaign Upload Link Studio</h1>
          <p>Submit campaign title and social links, then track live progress instantly.</p>
        </div>
        <button class="btn-refresh" @click="loadDashboard" :disabled="loading">{{ loading ? 'Loading...' : 'Refresh' }}</button>
      </div>

      <section class="card upload-studio-card">
        <div class="section-head">
          <h2>Upload Studio</h2>
          <span class="chip">DCM Submit Zone</span>
        </div>

        <div class="studio-layout">
          <article class="studio-panel selector-panel">
            <div class="panel-head">
              <h3>Step 1: Optional Sponsor/Campaign</h3>
              <span class="mini-chip">Search + Select</span>
            </div>

            <div class="actions selector-actions">
              <button class="btn-clear" type="button" @click="selectUploadCampaign('')">Use No Sponsor (Direct Upload)</button>
            </div>

            <div class="field field-wide">
              <label for="upload-search">Search Sponsor Name</label>
              <input id="upload-search" v-model.trim="uploadSearch" type="text" placeholder="Search sponsor or campaign" />
            </div>

            <div class="selector-table-wrap" v-if="uploadSelectionRows.length">
              <table class="selector-table">
                <thead>
                  <tr>
                    <th>Sponsor</th>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Risk</th>
                    <th>Target</th>
                    <th>Submitted</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="item in uploadSelectionRows" :key="`selector-${item.id}`">
                    <td>{{ item.sponsorName || '-' }}</td>
                    <td>{{ item.campaignName || '-' }}</td>
                    <td><span class="status-pill" :class="`s-${item.workStatus}`">{{ statusLabel(item.workStatus) }}</span></td>
                    <td>
                      <span
                        class="risk-badge"
                        :class="`risk-${item.deadlineRisk && item.deadlineRisk.level || 'none'}`"
                        :title="item.deadlineRisk && item.deadlineRisk.reasons && item.deadlineRisk.reasons[0] || ''"
                      >{{ riskLabel(item.deadlineRisk && item.deadlineRisk.level) }}</span>
                    </td>
                    <td>{{ Number(item.targetPlan && item.targetPlan.totalTarget || 0) }}</td>
                    <td>{{ Number(item.submittedCount || 0) }}</td>
                    <td><button class="btn-select" @click="selectUploadCampaign(item.id)">Select</button></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p v-else class="empty-mini">No running campaign found for search.</p>
          </article>

          <article class="studio-panel input-panel">
            <div class="panel-head">
              <h3>Step 2: Add Upload Links</h3>
              <span class="campaign-name-badge">{{ selectedUploadCampaign ? (selectedUploadCampaign.campaignName || '-') : 'No Sponsor (Direct Upload)' }}</span>
            </div>

            <div class="field field-wide">
              <label>Selected Sponsor</label>
              <input type="text" :value="selectedUploadCampaign ? (selectedUploadCampaign.sponsorName || '-') : 'No Sponsor (Direct Upload)'" disabled />
            </div>

            <div class="campaign-details-card" v-if="selectedUploadCampaign">
              <div class="details-head">
                <h4>Campaign Details</h4>
                <div class="details-actions">
                  <span class="status-pill" :class="`s-${selectedUploadCampaign.workStatus}`">{{ statusLabel(selectedUploadCampaign.workStatus) }}</span>
                  <span
                    v-if="selectedUploadCampaign.deadlineRisk && selectedUploadCampaign.deadlineRisk.level !== 'none'"
                    class="risk-badge"
                    :class="`risk-${selectedUploadCampaign.deadlineRisk.level}`"
                    :title="selectedUploadCampaign.deadlineRisk.reasons && selectedUploadCampaign.deadlineRisk.reasons[0] || ''"
                  >{{ riskLabel(selectedUploadCampaign.deadlineRisk.level) }}</span>
                  <button class="btn-view-all" type="button" @click="openUploadsModal">View All Uploads</button>
                </div>
              </div>

              <div class="details-grid">
                <div class="detail-item">
                  <span>Start Date</span>
                  <strong>{{ humanDate(selectedUploadCampaign.startDate) }}</strong>
                </div>
                <div class="detail-item">
                  <span>End Date</span>
                  <strong>{{ humanDate(selectedUploadCampaign.endDate) }}</strong>
                </div>
                <div class="detail-item">
                  <span>Categories</span>
                  <strong>{{ formatCategories(selectedUploadCampaign.contentCategory) }}</strong>
                </div>
                <div class="detail-item">
                  <span>Distribution Ratio</span>
                  <strong>{{ formatDistribution(selectedUploadCampaign) }}</strong>
                </div>
              </div>

              <div class="category-breakdown" v-if="selectedCampaignBreakdown.length">
                <p class="breakdown-title">Category Wise Divide</p>
                <div class="breakdown-table-wrap">
                  <table class="breakdown-table">
                    <thead>
                      <tr>
                        <th>Category</th>
                        <th>Ratio</th>
                        <th>Planned</th>
                        <th>Submitted</th>
                        <th>Remaining</th>
                        <th>Progress</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in selectedCampaignBreakdown" :key="`${selectedUploadCampaign.id}-${item.category}`">
                        <td class="cat-name-cell">{{ item.category }}</td>
                        <td>{{ item.ratioLabel }}%</td>
                        <td>{{ item.allocatedCount }}</td>
                        <td><strong class="submitted-count">{{ item.submittedCount }}</strong></td>
                        <td>{{ item.remainingCount }}</td>
                        <td class="progress-cell">
                          <div class="progress-ruler">
                            <div
                              class="progress-bar"
                              :style="{ width: item.progressPct + '%' }"
                              :class="{ 'bar-done': item.progressPct >= 100, 'bar-mid': item.progressPct >= 50 && item.progressPct < 100 }"
                            ></div>
                          </div>
                          <span class="progress-label">{{ item.progressPct }}%</span>
                        </td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr class="breakdown-total-row">
                        <td><strong>Total</strong></td>
                        <td>100%</td>
                        <td><strong>{{ breakdownTotals.allocated }}</strong></td>
                        <td><strong class="submitted-count">{{ breakdownTotals.submitted }}</strong></td>
                        <td><strong>{{ breakdownTotals.remaining }}</strong></td>
                        <td>
                          <div class="progress-ruler">
                            <div
                              class="progress-bar"
                              :style="{ width: breakdownTotals.overallPct + '%' }"
                              :class="{ 'bar-done': breakdownTotals.overallPct >= 100, 'bar-mid': breakdownTotals.overallPct >= 50 && breakdownTotals.overallPct < 100 }"
                            ></div>
                          </div>
                          <span class="progress-label">{{ breakdownTotals.overallPct }}%</span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>

            <div class="studio-grid">
              <div class="field field-wide">
                <label for="upload-title">Post Title</label>
                <input id="upload-title" v-model.trim="uploadForm.title" type="text" placeholder="Enter content title" />
              </div>

              <div class="field field-wide" v-if="selectedUploadCampaign && selectedCampaignCategories.length">
                <label for="upload-category">Category (Optional)</label>
                <select id="upload-category" v-model="uploadForm.category">
                  <option value="">Auto Select (Regular)</option>
                  <option v-for="category in selectedCampaignCategories" :key="`cat-${category}`" :value="category">{{ category }}</option>
                </select>
              </div>

              <div class="field">
                <label for="upload-youtube">YouTube Link</label>
                <input id="upload-youtube" v-model.trim="uploadForm.youtubeLink" type="url" placeholder="https://youtube.com/..." />
              </div>

              <div class="field">
                <label for="upload-facebook">Facebook Link</label>
                <input id="upload-facebook" v-model.trim="uploadForm.facebookLink" type="url" placeholder="https://facebook.com/..." />
              </div>

              <div class="field">
                <label for="upload-date">Upload Date</label>
                <input id="upload-date" v-model="uploadForm.uploadedDate" type="date" />
              </div>

              <div class="actions">
                <button class="btn-submit-links" :disabled="uploadSaving || isSponsorTargetReached" @click="submitUploadLinks">{{ uploadSaving ? 'Submitting...' : (selectedUploadCampaign ? 'Submit Campaign Upload Links' : 'Submit Direct Upload') }}</button>
                <button class="btn-clear" @click="resetUploadStudioSelection">Reset</button>
              </div>
              <p v-if="isSponsorTargetReached" class="empty-mini">Target reached for this campaign. You cannot submit more content.</p>
            </div>
          </article>
        </div>

        <div v-if="selectedUploadCampaign" class="target-strip">
          <div class="target-item status-item">
            <span>Work Status</span>
            <strong>{{ statusLabel(selectedUploadCampaign.workStatus) }}</strong>
          </div>
          <div class="target-item">
            <span>Target Between Date</span>
            <strong>{{ Number(selectedUploadCampaign.targetPlan && selectedUploadCampaign.targetPlan.totalTarget || 0) }}</strong>
          </div>
          <div class="target-item">
            <span>Submitted</span>
            <strong>{{ Number(selectedUploadCampaign.submittedCount || 0) }}</strong>
          </div>
          <div class="target-item">
            <span>Remaining</span>
            <strong>{{ Number(selectedUploadCampaign.remainingCount || 0) }}</strong>
          </div>
          <div class="target-item">
            <span>Progress</span>
            <strong>{{ Number(selectedUploadCampaign.targetPlan && selectedUploadCampaign.targetPlan.progressPercent || 0) }}%</strong>
          </div>
        </div>

        <div v-if="!directUploadsLoading && directUploads.length" class="direct-uploads-list">
          <p class="uploads-title">Your Recent Direct Uploads</p>
          <div class="uploads-table-wrap">
            <table class="uploads-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Links</th>
                  <th>Upload Date</th>
                  <th>Submitted At</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="upload in directUploads.slice(0, 10)" :key="upload.id">
                  <td>{{ upload.title || '-' }}</td>
                  <td class="upload-links">
                    <a v-if="upload.youtubeLink" :href="upload.youtubeLink" target="_blank" rel="noopener noreferrer">YouTube</a>
                    <a v-if="upload.facebookLink" :href="upload.facebookLink" target="_blank" rel="noopener noreferrer">Facebook</a>
                    <span v-if="!upload.youtubeLink && !upload.facebookLink">-</span>
                  </td>
                  <td>{{ humanDate(upload.uploadedDate) }}</td>
                  <td>{{ formatDateTime(upload.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <p v-if="!directUploadsLoading && !directUploads.length" class="empty-mini">No direct uploads yet. Submit your first one above!</p>
        <p v-if="directUploadsLoading" class="empty-mini">Loading your uploads...</p>

        <div v-if="selectedCampaignRecentUploads.length" class="recent-list">
          <p class="recent-title">Latest Submissions</p>
          <ul>
            <li v-for="item in selectedCampaignRecentUploads" :key="item.id || `${item.createdAt}-${item.title}`">
              <div>
                <strong>{{ item.title || '-' }}</strong>
                <small>{{ humanDate(item.uploadedDate) }} | {{ item.createdBy || '-' }} | {{ item.category || 'Auto' }}</small>
              </div>
              <div class="links">
                <a v-if="item.youtubeLink" :href="item.youtubeLink" target="_blank" rel="noopener noreferrer">YouTube</a>
                <a v-if="item.facebookLink" :href="item.facebookLink" target="_blank" rel="noopener noreferrer">Facebook</a>
                <small>{{ formatDateTime(item.createdAt) }}</small>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <div v-if="showUploadsModal && selectedUploadCampaign" class="upload-modal-backdrop" @click.self="closeUploadsModal">
        <div class="upload-modal">
          <div class="upload-modal-head">
            <div>
              <h3>All Submitted Upload Links</h3>
              <p>{{ selectedUploadCampaign.campaignName || '-' }} | {{ selectedUploadCampaign.sponsorName || '-' }}</p>
            </div>
            <button class="btn-close-modal" type="button" @click="closeUploadsModal">Close</button>
          </div>

          <div v-if="selectedCampaignUploads.length" class="upload-modal-table-wrap">
            <table class="upload-modal-table">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Category</th>
                  <th>Links</th>
                  <th>Upload Date</th>
                  <th>Uploaded By</th>
                  <th>Submitted At</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="item in selectedCampaignUploads" :key="item.id || `${item.createdAt}-${item.title}-${item.youtubeLink}`">
                  <td>{{ item.title || '-' }}</td>
                  <td>{{ item.category || 'Auto' }}</td>
                  <td class="modal-links">
                    <a v-if="item.youtubeLink" :href="item.youtubeLink" target="_blank" rel="noopener noreferrer">YouTube</a>
                    <a v-if="item.facebookLink" :href="item.facebookLink" target="_blank" rel="noopener noreferrer">Facebook</a>
                    <span v-if="!item.youtubeLink && !item.facebookLink">-</span>
                  </td>
                  <td>{{ humanDate(item.uploadedDate) }}</td>
                  <td>{{ item.createdBy || '-' }}</td>
                  <td>{{ formatDateTime(item.createdAt) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p v-else class="empty-mini">No uploads submitted yet for this campaign.</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MenuBar from './MenuBar.vue';
import sponsorService from '../services/sponsorService';
import api from '../api';
import { campaignStatusLabel, canPublishCampaign } from '../utils/campaignLifecycle';

export default {
  name: 'UploadStudio',
  components: { MenuBar },
  data() {
    return {
      loading: false,
      message: '',
      campaigns: [],
      userRole: '',
      isSuperUser: false,
      uploadSearch: '',
      uploadForm: {
        campaignId: '',
        title: '',
        category: '',
        youtubeLink: '',
        facebookLink: '',
        uploadedDate: '',
      },
      uploadSaving: false,
      showUploadsModal: false,
      directUploads: [],
      directUploadsLoading: false,
    };
  },
  computed: {
    canSubmitCampaignLinks() {
      const role = String(this.userRole || '').toLowerCase();
      return this.isSuperUser || role === 'dcm';
    },
    runningCampaignOptions() {
      return this.campaigns.filter(item => !item.isArchived && canPublishCampaign(item.workStatus));
    },
    selectedUploadCampaign() {
      return this.campaigns.find(item => item.id === this.uploadForm.campaignId) || null;
    },
    uploadSelectionRows() {
      const keyword = String(this.uploadSearch || '').trim().toLowerCase();
      return this.runningCampaignOptions.filter(item => {
        if (!keyword) return true;
        const sponsor = String(item.sponsorName || '').toLowerCase();
        const campaign = String(item.campaignName || '').toLowerCase();
        return sponsor.includes(keyword) || campaign.includes(keyword);
      });
    },
    selectedCampaignRecentUploads() {
      return this.selectedCampaignUploads.slice(0, 6);
    },
    selectedCampaignUploads() {
      const selected = this.selectedUploadCampaign;
      if (!selected || !Array.isArray(selected.channelUploads)) return [];
      return [...selected.channelUploads].sort((a, b) => {
        const aTime = new Date(a.createdAt || a.uploadedDate || 0).getTime();
        const bTime = new Date(b.createdAt || b.uploadedDate || 0).getTime();
        return bTime - aTime;
      });
    },
    selectedCampaignCategories() {
      const selected = this.selectedUploadCampaign;
      if (!selected) return [];
      const base = Array.isArray(selected.contentCategory)
        ? selected.contentCategory.filter(item => String(item || '').trim())
        : [];
      const distributionItems = selected.contentDistribution && Array.isArray(selected.contentDistribution.items)
        ? selected.contentDistribution.items
        : [];
      const baseLower = new Set(base.map(item => String(item).toLowerCase()));
      const extras = distributionItems
        .map(item => String(item && item.category || '').trim())
        .filter(cat => cat && !baseLower.has(cat.toLowerCase()));
      return [...base, ...extras];
    },
    selectedCampaignBreakdown() {
      return this.buildCategoryBreakdown(this.selectedUploadCampaign);
    },
    breakdownTotals() {
      const rows = this.selectedCampaignBreakdown;
      let allocated = 0;
      let submitted = 0;
      let remaining = 0;
      let hasNumbers = false;
      rows.forEach(item => {
        const a = Number(item.allocatedCount);
        const s = Number(item.submittedCount);
        const r = Number(item.remainingCount);
        if (!Number.isNaN(a) && item.allocatedCount !== '-') { allocated += a; hasNumbers = true; }
        if (!Number.isNaN(s)) submitted += s;
        if (!Number.isNaN(r) && item.remainingCount !== '-') remaining += r;
      });
      const overallPct = hasNumbers && allocated > 0 ? Math.min(Math.round((submitted / allocated) * 100), 100) : 0;
      return {
        allocated: hasNumbers ? allocated : '-',
        submitted,
        remaining: hasNumbers ? remaining : '-',
        overallPct,
      };
    },
    isSponsorTargetReached() {
      const selected = this.selectedUploadCampaign;
      if (!selected) return false;
      const totalTarget = Number(selected.targetPlan && selected.targetPlan.totalTarget || 0);
      const remaining = Number(selected.remainingCount || 0);
      return totalTarget > 0 && remaining <= 0;
    },
  },
  mounted() {
    this.loadUserContext();
    this.loadDashboard();
    this.loadDirectUploads();
  },
  methods: {
    async loadUserContext() {
      try {
        const response = await api.get('/check');
        this.userRole = response.data.role || '';
        this.isSuperUser = !!response.data.isSuperUser;
      } catch (error) {
        this.userRole = '';
        this.isSuperUser = false;
      }
    },
    async loadDashboard() {
      this.loading = true;
      this.message = '';
      try {
        const response = await sponsorService.getCampaignDashboardData();
        this.campaigns = response.items || [];
        const campaignId = this.$route.query && this.$route.query.campaignId;
        if (campaignId && this.campaigns.some(item => item.id === campaignId)) {
          this.uploadForm.campaignId = campaignId;
        }

        const selectedCampaign = this.campaigns.find(item => item.id === this.uploadForm.campaignId);
        if (selectedCampaign && this.uploadForm.category) {
          const validCategory = (selectedCampaign.contentCategory || []).some(
            item => this.normalizeCategoryKey(item) === this.normalizeCategoryKey(this.uploadForm.category)
          );
          if (!validCategory) {
            this.uploadForm.category = '';
          }
        }
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error) || 'Failed to load upload studio data';
      } finally {
        this.loading = false;
      }
    },
    selectUploadCampaign(campaignId) {
      this.uploadForm.campaignId = campaignId;
      this.uploadForm.category = '';
      this.closeUploadsModal();
    },
    resetUploadStudioSelection() {
      this.uploadForm = {
        campaignId: '',
        title: '',
        category: '',
        youtubeLink: '',
        facebookLink: '',
        uploadedDate: '',
      };
      this.uploadSearch = '';
      this.closeUploadsModal();
    },
    openUploadsModal() {
      this.showUploadsModal = true;
    },
    closeUploadsModal() {
      this.showUploadsModal = false;
    },
    statusLabel(status) {
      return campaignStatusLabel(status);
    },
    riskLabel(level) {
      const map = { critical: 'Critical', at_risk: 'At Risk', warning: 'Warning', on_track: 'On Track', none: 'N/A' };
      return map[level] || 'N/A';
    },
    humanDate(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    },
    formatCategories(value) {
      if (!Array.isArray(value) || !value.length) return '-';
      return value.join(', ');
    },
    formatDistribution(campaign) {
      const distribution = campaign && campaign.contentDistribution;
      if (distribution && Array.isArray(distribution.items) && distribution.items.length) {
        return distribution.items.map(item => `${item.category}: ${item.percent}%`).join(', ');
      }
      return (campaign && campaign.distributionRatio) || '-';
    },
    parsePositiveNumber(value) {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
    },
    normalizeCategoryKey(value) {
      return String(value || '').trim().toLowerCase();
    },
    resolveAutoCategory(campaign) {
      const categories = Array.isArray(campaign && campaign.contentCategory)
        ? campaign.contentCategory.filter(item => String(item || '').trim())
        : [];
      if (!categories.length) return '';

      const breakdown = this.buildCategoryBreakdown(campaign);
      const nextCategory = breakdown
        .filter(item => typeof item.remainingCount === 'number')
        .sort((a, b) => {
          if (b.remainingCount === a.remainingCount) {
            return a.submittedCount - b.submittedCount;
          }
          return b.remainingCount - a.remainingCount;
        })
        .find(item => item.remainingCount > 0);

      if (nextCategory && nextCategory.category) {
        return nextCategory.category;
      }

      const submittedCounter = breakdown.reduce((acc, item) => {
        if (typeof item.submittedCount === 'number') {
          acc[item.category] = item.submittedCount;
        }
        return acc;
      }, {});

      return categories
        .slice()
        .sort((a, b) => {
          const aCount = submittedCounter[a] || 0;
          const bCount = submittedCounter[b] || 0;
          if (aCount === bCount) return 0;
          return aCount - bCount;
        })[0];
    },
    parseDistributionRatios(value, expectedCount) {
      const matches = String(value || '').match(/\d+(?:\.\d+)?/g) || [];
      const ratios = matches.map(item => Number(item)).filter(item => Number.isFinite(item) && item > 0);
      if (!ratios.length) return [];
      if (expectedCount && ratios.length < expectedCount) {
        while (ratios.length < expectedCount) {
          ratios.push(0);
        }
      }
      return expectedCount ? ratios.slice(0, expectedCount) : ratios;
    },
    buildCategoryBreakdown(campaign) {
      if (!campaign) return [];

      const base = Array.isArray(campaign.contentCategory)
        ? campaign.contentCategory.filter(item => String(item || '').trim())
        : [];

      const structuredItemsEarly = campaign.contentDistribution && Array.isArray(campaign.contentDistribution.items)
        ? campaign.contentDistribution.items
        : [];

      const baseLowerSet = new Set(base.map(item => String(item).toLowerCase()));
      const extras = structuredItemsEarly
        .map(item => String(item && item.category || '').trim())
        .filter(cat => cat && !baseLowerSet.has(cat.toLowerCase()));

      const categories = [...base, ...extras];

      if (!categories.length) return [];

      const submittedByCategory = (Array.isArray(campaign.channelUploads) ? campaign.channelUploads : []).reduce((acc, upload) => {
        const key = this.normalizeCategoryKey(upload && upload.category);
        if (!key) return acc;
        acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      const totalContent = this.parsePositiveNumber(campaign.numberOfContent || (campaign.targetPlan && campaign.targetPlan.totalTarget));
      const structuredItems = campaign.contentDistribution && Array.isArray(campaign.contentDistribution.items)
        ? campaign.contentDistribution.items
        : [];

      let ratios = [];
      if (structuredItems.length) {
        const structuredMap = {};
        structuredItems.forEach(item => {
          const key = String(item && item.category || '').trim().toLowerCase();
          if (!key) return;
          structuredMap[key] = this.parsePositiveNumber(item.percent);
        });
        ratios = categories.map(category => structuredMap[String(category).toLowerCase()] || 0);
      }

      if (!ratios.some(item => item > 0)) {
        ratios = this.parseDistributionRatios(campaign.distributionRatio, categories.length);
      }

      let weights = ratios.length ? ratios : categories.map(() => 1);
      const totalWeight = weights.reduce((sum, item) => sum + item, 0);
      if (!totalWeight) {
        weights = categories.map(() => 1);
      }

      const normalizedTotalWeight = weights.reduce((sum, item) => sum + item, 0);
      let remaining = totalContent;

      return categories.map((category, index) => {
        const weight = weights[index] || 0;
        const rawCount = totalContent > 0 && normalizedTotalWeight > 0
          ? (index === categories.length - 1 ? remaining : Math.round((totalContent * weight) / normalizedTotalWeight))
          : 0;
        const allocatedCount = totalContent > 0
          ? Math.max(index === categories.length - 1 ? remaining : Math.min(rawCount, remaining), 0)
          : 0;
        remaining = Math.max(remaining - allocatedCount, 0);

        const submittedCount = submittedByCategory[this.normalizeCategoryKey(category)] || 0;
        const progressPct = totalContent > 0 && allocatedCount > 0
          ? Math.min(Math.round((submittedCount / allocatedCount) * 100), 100)
          : 0;

        return {
          category,
          ratioLabel: normalizedTotalWeight > 0 ? `${weight}` : '-',
          allocatedCount: totalContent > 0 ? allocatedCount : '-',
          submittedCount,
          remainingCount: totalContent > 0
            ? Math.max(allocatedCount - submittedCount, 0)
            : '-',
          progressPct,
        };
      });
    },
    async submitUploadLinks() {
      if (!this.canSubmitCampaignLinks) return;
      if (!this.uploadForm.title.trim()) return (this.message = 'Title is required');
      if (!this.uploadForm.youtubeLink.trim() && !this.uploadForm.facebookLink.trim()) {
        return (this.message = 'Add at least one link');
      }

      this.uploadSaving = true;
      this.message = '';
      try {
        const selectedCampaign = this.selectedUploadCampaign;
        if (selectedCampaign) {
          if (this.isSponsorTargetReached) {
            this.message = 'Target reached for this campaign. You cannot submit more content.';
            return;
          }

          const selectedCategory = this.uploadForm.category || this.resolveAutoCategory(selectedCampaign);
          await sponsorService.createCampaignChannelUpload(this.uploadForm.campaignId, {
            title: this.uploadForm.title.trim(),
            category: selectedCategory || '',
            youtubeLink: this.uploadForm.youtubeLink.trim(),
            facebookLink: this.uploadForm.facebookLink.trim(),
            uploadedDate: this.uploadForm.uploadedDate || '',
          });
        } else {
          await sponsorService.createDirectUpload({
            title: this.uploadForm.title.trim(),
            youtubeLink: this.uploadForm.youtubeLink.trim(),
            facebookLink: this.uploadForm.facebookLink.trim(),
            uploadedDate: this.uploadForm.uploadedDate || '',
          });
        }

        const keepCampaignId = this.uploadForm.campaignId;
        this.uploadForm = {
          campaignId: keepCampaignId,
          title: '',
          category: '',
          youtubeLink: '',
          facebookLink: '',
          uploadedDate: '',
        };

        this.message = selectedCampaign
          ? 'Campaign upload links submitted successfully'
          : 'Direct upload submitted successfully';

        if (selectedCampaign) {
          await this.loadDashboard();
        } else {
          await this.loadDirectUploads();
        }
      } catch (error) {
        this.message = (error.response && error.response.data && error.response.data.error)
          || (this.selectedUploadCampaign ? 'Failed to submit campaign links' : 'Failed to submit direct upload');
      } finally {
        this.uploadSaving = false;
      }
    },
    formatDateTime(value) {
      if (!value) return '-';
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    },
    async loadDirectUploads() {
      this.directUploadsLoading = true;
      try {
        const response = await sponsorService.listDirectUploads();
        this.directUploads = response.items || [];
      } catch (error) {
        this.directUploads = [];
      } finally {
        this.directUploadsLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.upload-studio-page { display: flex; min-height: 100vh; background: radial-gradient(circle at 10% 10%, #e6f4f2 0%, transparent 30%), radial-gradient(circle at 90% 20%, #fff3e0 0%, transparent 34%), #f4f7fb; }
.content { flex: 1; margin-left: 250px; padding: 24px; }
.page-header { display: flex; justify-content: space-between; align-items: flex-start; gap: 14px; margin-bottom: 14px; }
.page-header h1 { margin: 0; color: #1f2d3a; }
.page-header p { margin: 6px 0 0; color: #607382; }
.btn-refresh { border: 1px solid #0e8e7f; background: linear-gradient(135deg, #12a594 0%, #0e8e7f 100%); color: #fff; border-radius: 10px; padding: 8px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.card { background: #fff; border: 1px solid #d8e1e8; border-radius: 14px; padding: 14px; box-shadow: 0 12px 24px rgba(14, 22, 35, 0.06); }
.upload-studio-card { background: linear-gradient(130deg, #f2fffd 0%, #ffffff 35%, #fff8ec 100%); border-color: #b9e3dd; }
.section-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; }
.section-head h2 { margin: 0; color: #1f2d3a; }
.chip { background: #e6f4f2; color: #14584e; border: 1px solid #b2ddd7; border-radius: 999px; padding: 4px 10px; font-size: 11px; font-weight: 700; }
.tab-switcher { display: flex; gap: 8px; margin-bottom: 16px; border-bottom: 2px solid #e0e8ed; }
.tab-btn { border: none; background: transparent; padding: 10px 16px; font-size: 13px; font-weight: 700; color: #607382; cursor: pointer; border-bottom: 3px solid transparent; transition: all 0.2s ease; }
.tab-btn:hover { color: #2c3e50; }
.tab-btn.active { color: #12a594; border-bottom-color: #12a594; }
.studio-layout { display: grid; grid-template-columns: 1.1fr 1fr; gap: 12px; }
.studio-panel { border: 1px solid #cce4df; border-radius: 12px; background: #fff; padding: 10px; }
.direct-upload-section { width: 100%; }
.direct-upload-panel { border: 1px solid #cce4df; border-radius: 12px; background: #fff; padding: 10px; }
.direct-upload-form { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 16px; }
.direct-uploads-list { margin-top: 16px; border-top: 1px dashed #bcd8d3; padding-top: 12px; }
.uploads-title { margin: 0 0 8px; color: #274657; font-weight: 700; font-size: 13px; }
.uploads-table-wrap { overflow-x: auto; border: 1px solid #d8e8e4; border-radius: 10px; }
.uploads-table { width: 100%; min-width: 700px; border-collapse: collapse; }
.uploads-table th, .uploads-table td { padding: 9px; border-bottom: 1px solid #e6f0ee; text-align: left; font-size: 12px; color: #314a5a; }
.uploads-table th { background: #f4faf8; color: #24475b; font-weight: 700; }
.upload-links { display: flex; align-items: center; gap: 8px; }
.upload-links a { font-size: 11px; font-weight: 700; color: #0f6aa8; }
.panel-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.panel-head h3 { margin: 0; color: #204357; font-size: 14px; }
.mini-chip { border: 1px solid #c6dade; border-radius: 999px; padding: 3px 8px; color: #4e6c7e; font-size: 10px; font-weight: 700; background: #f7fbfd; }
.mini-chip.active { border-color: #9bcac2; color: #175e52; background: #edfbf7; }
.campaign-details-card { margin-bottom: 12px; border: 1px solid #d8e8e4; border-radius: 12px; background: linear-gradient(180deg, #fbfffe 0%, #f6fbfa 100%); padding: 12px; }
.details-head { display: flex; justify-content: space-between; align-items: center; gap: 8px; margin-bottom: 10px; }
.details-head h4 { margin: 0; color: #214255; font-size: 13px; }
.details-actions { display: flex; align-items: center; gap: 8px; }
.btn-view-all { border: 1px solid #8ebcb4; background: #f2fbf8; color: #1c6157; border-radius: 8px; padding: 5px 10px; font-size: 11px; font-weight: 700; cursor: pointer; }
.details-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin-bottom: 12px; }
.detail-item { border: 1px solid #e1efec; border-radius: 10px; background: #fff; padding: 10px; }
.detail-item span { display: block; color: #67808f; font-size: 11px; font-weight: 700; }
.detail-item strong { display: block; margin-top: 4px; color: #213847; font-size: 13px; }
.category-breakdown { border-top: 1px dashed #c8ddd8; padding-top: 10px; }
.breakdown-title { margin: 0 0 8px; color: #274657; font-weight: 700; font-size: 12px; }
.breakdown-table-wrap { overflow-x: auto; }
.breakdown-table { width: 100%; border-collapse: collapse; }
.breakdown-table th, .breakdown-table td { padding: 8px; border-bottom: 1px solid #e6f0ee; text-align: left; font-size: 12px; color: #314a5a; }
.breakdown-table th { background: #f4faf8; color: #24475b; font-weight: 700; }
.cat-name-cell { font-weight: 600; color: #1d3a4e; }
.submitted-count { color: #10623c; font-size: 13px; }
.progress-cell { min-width: 110px; }
.progress-ruler { height: 6px; background: #dff0eb; border-radius: 999px; overflow: hidden; margin-bottom: 3px; }
.progress-bar { height: 100%; background: #64c8a8; border-radius: 999px; transition: width 0.3s ease; }
.progress-bar.bar-mid { background: #f59e0b; }
.progress-bar.bar-done { background: #22c55e; }
.progress-label { font-size: 10px; color: #4e7a69; font-weight: 700; }
.breakdown-total-row td { background: #eaf5f1; border-top: 2px solid #9bd4c2; font-size: 12px; }
.breakdown-total-row .progress-label { color: #175940; }
.campaign-name-badge { display: inline-flex; align-items: center; background: #1a3d5c; color: #fff; border-radius: 8px; padding: 4px 12px; font-size: 13px; font-weight: 800; letter-spacing: 0.01em; max-width: 260px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.studio-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
.field { display: flex; flex-direction: column; gap: 4px; }
.field-wide { grid-column: span 2; }
.field label { color: #607382; font-size: 12px; font-weight: 700; }
.field input, .field select { border: 1px solid #c9d6df; border-radius: 8px; padding: 8px 10px; font-size: 12px; color: #2b3f4f; background: #fff; }
.selector-table-wrap { margin-top: 8px; max-height: 280px; overflow: auto; border: 1px solid #d7e7e2; border-radius: 10px; }
.selector-table { width: 100%; border-collapse: collapse; min-width: 560px; }
.selector-table th, .selector-table td { padding: 8px; font-size: 12px; border-bottom: 1px solid #e3efeb; color: #324b5d; text-align: left; }
.selector-table th { background: #f4faf8; color: #24475b; font-weight: 700; }
.btn-select { border: 1px solid #2c8a7d; background: #ffffff; color: #1f766a; border-radius: 7px; padding: 5px 8px; font-size: 11px; font-weight: 700; cursor: pointer; }
.actions { display: flex; gap: 8px; justify-content: flex-end; }
.btn-submit-links { border: 1px solid #0e8e7f; background: linear-gradient(135deg, #12a594 0%, #0e8e7f 100%); color: #fff; border-radius: 10px; padding: 10px 14px; font-size: 12px; font-weight: 700; cursor: pointer; }
.btn-clear { border: 1px solid #95a8b6; background: #f6f9fb; color: #334a59; border-radius: 8px; padding: 8px 12px; font-size: 12px; font-weight: 700; cursor: pointer; }
.target-strip { margin-top: 12px; display: grid; grid-template-columns: repeat(5, minmax(0, 1fr)); gap: 10px; }
.target-item { border: 1px solid #cde3df; background: #ffffff; border-radius: 10px; padding: 10px; }
.target-item span { display: block; color: #5d7483; font-size: 11px; font-weight: 700; }
.target-item strong { color: #1f3d4f; font-size: 20px; }
.status-item strong { font-size: 16px; }
.status-pill { display: inline-block; border-radius: 999px; padding: 4px 8px; font-size: 11px; font-weight: 700; }
.status-pill.s-creative_approved { background: #f3f0ff; color: #6941c6; }
.status-pill.s-publishing { background: #e0f2fe; color: #075985; }
.status-pill.s-completed { background: #dcfce7; color: #166534; }
/* --- Risk Badge --- */
.risk-badge { display: inline-block; border-radius: 999px; padding: 3px 9px; font-size: 11px; font-weight: 700; white-space: nowrap; }
.risk-critical { background: #fff1f0; color: #a8200b; border: 1px solid #ffc9c9; }
.risk-at_risk { background: #fff6ed; color: #883f00; border: 1px solid #ffd8a8; }
.risk-warning { background: #fffbe0; color: #6e5300; border: 1px solid #ffe168; }
.risk-on_track { background: #f0faf6; color: #136b46; border: 1px solid #99dec5; }
.risk-none { background: #f3f5f8; color: #556b7a; border: 1px solid #d4dde5; }
.recent-list { margin-top: 12px; border-top: 1px dashed #bcd8d3; padding-top: 10px; }
.recent-title { margin: 0 0 6px; color: #274657; font-weight: 700; font-size: 12px; }
.recent-list ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 8px; }
.recent-list li { border: 1px solid #d8e8e4; background: #fff; border-radius: 9px; padding: 8px 10px; display: flex; justify-content: space-between; gap: 8px; align-items: center; }
.recent-list li strong { display: block; color: #1f3748; font-size: 12px; }
.recent-list li small { color: #6a7f8e; font-size: 11px; }
.links { display: flex; align-items: center; gap: 8px; }
.links a { font-size: 11px; font-weight: 700; color: #0f6aa8; }
.upload-modal-backdrop { position: fixed; inset: 0; background: rgba(20, 32, 44, 0.45); display: flex; justify-content: center; align-items: center; z-index: 90; padding: 16px; }
.upload-modal { width: min(1120px, 96vw); max-height: 90vh; overflow: auto; background: #ffffff; border: 1px solid #cddfe6; border-radius: 14px; box-shadow: 0 20px 40px rgba(13, 29, 48, 0.24); padding: 14px; }
.upload-modal-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 10px; }
.upload-modal-head h3 { margin: 0; color: #1f2f3f; }
.upload-modal-head p { margin: 4px 0 0; color: #5d7483; font-size: 12px; }
.btn-close-modal { border: 1px solid #96adba; background: #f6f9fb; color: #334a59; border-radius: 8px; padding: 6px 10px; font-size: 12px; font-weight: 700; cursor: pointer; }
.upload-modal-table-wrap { overflow: auto; border: 1px solid #dae8ee; border-radius: 10px; }
.upload-modal-table { width: 100%; min-width: 900px; border-collapse: collapse; }
.upload-modal-table th, .upload-modal-table td { padding: 9px; border-bottom: 1px solid #e6eef2; text-align: left; font-size: 12px; color: #2f4555; vertical-align: top; }
.upload-modal-table th { background: #f4faf8; color: #23435a; font-weight: 700; }
.modal-links { display: flex; align-items: center; gap: 8px; }
.modal-links a { font-size: 11px; font-weight: 700; color: #0f6aa8; }
.empty-mini { margin: 8px 0 2px; color: #6a7f8e; font-size: 12px; }
@media (max-width: 1024px) { .content { margin-left: 200px; } .studio-layout { grid-template-columns: 1fr; } .target-strip { grid-template-columns: repeat(2, minmax(0, 1fr)); } }
@media (max-width: 768px) { .content { margin-left: 0; padding: 14px; } .page-header { flex-direction: column; align-items: stretch; } .details-grid, .studio-grid { grid-template-columns: 1fr; } .field-wide { grid-column: span 1; } .target-strip { grid-template-columns: 1fr; } .recent-list li { flex-direction: column; align-items: flex-start; } .actions { justify-content: flex-start; } .details-actions { width: 100%; justify-content: space-between; } .upload-modal { width: 100%; max-height: 94vh; } }
</style>
